/**
 * The pure ownership rules a play-scoped picker (`SequenceMappingDrawer`'s
 * sequence list, `QualifyStep`'s "Add segmentation" list) and its in-play
 * save gate need, over `GET /library?playId=`'s rows
 * (`PlaybooksSequenceSummary` / `PlaybooksSegmentationSummary`, each carrying
 * `owningPlayId` / `forkSourceId` / `archivedAt`). Plan 2026-09-27-006 U7a,
 * rewritten by plan 2026-09-28-006 U3a (rulebook R11, R18, R19).
 *
 * Inside a play, a copy IS a picker row — nothing is hidden and nothing is
 * substituted (R11): the rep gets exactly the row they click, and the
 * ladder stores exactly that id. A row is one of:
 *  - a LIBRARY row (`owningPlayId === null`) — untagged. Picking it points
 *    the group at the Library piece, even when this play also holds copies
 *    of it.
 *  - a COPY (`owningPlayId === playId && forkSourceId !== null`) — one of
 *    this play's own versions of a Library piece; a play may hold several
 *    (one per group that was edited, R3). Tagged `'Edited in this play'` and
 *    told apart from same-named rows by {@link ownedRowUseText} (R19).
 *  - a PLAY-CREATED ORIGINAL (`owningPlayId === playId && forkSourceId ===
 *    null`) — tagged `'Only in this play'`.
 *  - ARCHIVED IN LIBRARY (`archivedAt !== null`) — a Library piece an admin
 *    archived that this play's ladder still references; returned only so
 *    the group can show its name. Never a choosable row (R18).
 * Another play's rows are never shown (R1).
 *
 * Pure, DOM-free, zero imports but a type-only one from `./schemas` (module-
 * graph-pinned) — so `bun test --isolate` can exercise every helper directly.
 */
import type { PlaybookRung, PlaybooksSegmentationSummary, PlaybooksSequenceSummary } from './schemas';

/** The ownership fields every library row carries — segmentation or sequence, doesn't matter which. */
export type PlaybooksOwnershipRow = Pick<
  PlaybooksSegmentationSummary | PlaybooksSequenceSummary,
  'id' | 'owningPlayId' | 'forkSourceId' | 'archivedAt'
>;

/**
 * [R18] True when `row` is a Library piece an admin archived that this play's
 * ladder still references — `GET /library?playId=` returns it (with
 * `archivedAt` set) only so the group can still show its name. Shown tagged
 * "Archived in Library", never a choosable picker row, never offered "update
 * the playbook".
 */
export function isArchivedInLibrary<T extends PlaybooksOwnershipRow>(row: T): boolean {
  return row.archivedAt !== null;
}

/** True when `row` is this play's OWN identity — a copy, or a play-created original. */
export function isOwnedByPlay<T extends PlaybooksOwnershipRow>(row: T, playId: string): boolean {
  return row.owningPlayId === playId;
}

/**
 * [R11] The rows a play's picker renders: every Library row and every row
 * this play owns (copies included, each its own row) — minus anything
 * archived in the Library ([R18], never choosable) and anything another play
 * owns ([R1]).
 */
export function pickerRows<T extends PlaybooksOwnershipRow>(rows: readonly T[], playId: string): T[] {
  return rows.filter(
    (row) => !isArchivedInLibrary(row) && (row.owningPlayId === null || row.owningPlayId === playId),
  );
}

export type PlaybooksPickerTag = 'Edited in this play' | 'Only in this play' | null;

/**
 * [R19] The picker badge for one row: this play's copy → `'Edited in this
 * play'`; a play-created original → `'Only in this play'`; a Library row (or
 * anything this play does not own) → `null`. A Library row is never tagged on
 * account of a copy of it existing — the copy is its own row.
 */
export function pickerTag<T extends PlaybooksOwnershipRow>(row: T, playId: string): PlaybooksPickerTag {
  if (row.owningPlayId !== playId) return null;
  return row.forkSourceId !== null ? 'Edited in this play' : 'Only in this play';
}

/**
 * [R18][R19] The secondary line under an owned picker row — which groups of
 * this play use it, so two same-named rows ("Tradeshow follow-up" the
 * Library piece, "Tradeshow follow-up" this play's copy) can be told apart
 * (R19), and a copy whose group moved away stays visible as an orphan (R18):
 *
 *  - "Used by General DQ (Attended), Everything Else (Attended)" — every
 *    group whose segment (`kind: 'segmentation'`, matched on
 *    `rung.segmentationId`) or sequence (`kind: 'sequence'`, matched on
 *    `rung.mapping?.sequenceId`) is `rowId`, in rung order, named by the
 *    group's segment name;
 *  - "Not used by any group yet" — no group uses it.
 *
 * A group whose segment name is not in `segmentationNameById` is counted,
 * never named by id (R20): "Used by General DQ (Attended) and 1 other group",
 * or "Used by 2 groups" when none is named.
 */
export function ownedRowUseText(
  rowId: string,
  rungs: readonly PlaybookRung[],
  segmentationNameById: ReadonlyMap<string, string>,
  kind: 'segmentation' | 'sequence',
): string {
  const using = rungs.filter((rung) =>
    kind === 'segmentation' ? rung.segmentationId === rowId : rung.mapping?.sequenceId === rowId,
  );
  if (using.length === 0) return 'Not used by any group yet';
  const names: string[] = [];
  let unnamed = 0;
  for (const rung of using) {
    const name = segmentationNameById.get(rung.segmentationId);
    if (name === undefined) unnamed += 1;
    else names.push(name);
  }
  const groups = (n: number): string => (n === 1 ? 'group' : 'groups');
  if (names.length === 0) return `Used by ${unnamed} ${groups(unnamed)}`;
  const named = names.join(', ');
  return unnamed === 0 ? `Used by ${named}` : `Used by ${named} and ${unnamed} other ${groups(unnamed)}`;
}
